import styled, { css, keyframes } from 'styled-components'
import { srOnly } from '@/shared/styles/mixins'
import { ButtonHTMLAttributes } from 'react'

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  $offsetbottom?: number
  $visible?: string
  $animate?: string
  dataqaid?: string
}

const bounce = keyframes`
  from {
    animation-timing-function: cubic-bezier(0.215, 0.61, 0.355, 1);
    transform: translate3d(0, 0, 0);
    visibility: hidden;
    opacity: 0;
  }

  20% {
    animation-timing-function: linear;
    transform: translate3d(0, 0, 0);
    visibility: visible;
    opacity: 1;
  }

  53%,
  to {
    animation-timing-function: cubic-bezier(0.215, 0.61, 0.355, 1);
    transform: translate3d(0, 0, 0);
  }

  40%,
  43% {
      animation-timing-function: cubic-bezier(0.755, 0.05, 0.855, 0.06);
      transform: translate3d(0, -30px, 0) scaleY(1.1);
  }

  70% {
      animation-timing-function: cubic-bezier(0.755, 0.05, 0.855, 0.06);
      transform: translate3d(0, -15px, 0) scaleY(1.05);
  }

  80% {
      transition-timing-function: cubic-bezier(0.215, 0.61, 0.355, 1);
      transform: translate3d(0, 0, 0) scaleY(0.95);
  }

  90% {
      transform: translate3d(0, -4px, 0) scaleY(1.02);
  }

  to {
      visibility: visible;
      opacity: 1;
      transform: translate3d(0, 0, 0);
  }
`

const woble = keyframes`
  from,
  to {
    transform-origin: bottom center;
    transform: scale(1);
  }

  40% {
    transform-origin: bottom center;
    transform: scale(1.25);
  }

  75% {
      transform-origin: bottom center;
      transform: scale(0.85);
  }
`

const bounceAnimationMobile = css`
  animation: ${bounce} 0.5s;
  animation-fill-mode: forwards;
`

const bounceAnimationDesk = css`
  animation: ${woble} 0.5s;
`

const spin = keyframes`
  to {
    transform: rotate(360deg);
  }
`

export const Spinner = styled.span`
  display: inline-block;
  width: 18px;
  height: 18px;
  border: 2px solid ${({ theme }) => theme.colors.getColor('neutral-purewhite')};
  border-top-color: transparent;
  border-radius: 50%;
  animation: ${spin} 0.7s linear infinite;
  flex-shrink: 0;
`

export const Button = styled.button.withConfig({
  shouldForwardProp: (prop) =>
    !['$offsetbottom', '$visible', '$animate'].includes(prop)
})<ButtonProps>`
  ${({ theme, $offsetbottom = 0, $animate, $visible }) => css`
    cursor: pointer;

    position: fixed;
    align-items: center;
    right: 20px;
    bottom: ${$visible === 'true'
      ? `calc(20px + ${$offsetbottom}px + env(safe-area-inset-bottom))`
      : `${$offsetbottom}px`};
    background: ${theme.colors.getColor('primary')};
    border: 0;
    border-radius: 100%;
    display: ${$visible === 'true' ? 'inline-flex' : 'none'};
    min-width: auto;
    min-height: auto;
    height: 55px;
    font-size: 0.875rem;
    z-index: 4;
    transform: translate3d(0, 0, 0);
    visibility: ${$visible === 'true' ? 'visible' : 'hidden'};
    opacity: ${$visible === 'true' ? 1 : 0};
    ${$animate === 'true' && $visible === 'true'
      ? bounceAnimationMobile
      : 'none'};

    @media screen and (min-width: ${theme.breakpoints.get('small', 'max')}px) {
      right: 20px;
      bottom: ${$visible === 'true' ? `${$offsetbottom}px` : 'initial'};
      border-bottom: 2px solid ${theme.colors.getColor('primary-base')};
      border-radius: 100%;
      display: ${$visible === 'true' ? 'inline-flex' : 'none'};
      padding: 0;
      visibility: ${$visible === 'true' ? 'visible' : 'hidden'};
      opacity: ${$visible === 'true' ? 1 : 0};
      transform: translate3d(0, 0, 0);
      ${$animate === 'true' ? bounceAnimationDesk : ''};
    }

    @media screen and (min-width: ${theme.breakpoints.get('large', 'min')}px) {
      border-radius: 4px 4px 0 0;
      padding: 8px 12px;
      visibility: visible;
      min-width: 200px;
    }
  `}
`

export const TextButton = styled.p`
  ${({ theme }) => css`
    position: relative;
    padding-left: 10px;
    font-weight: ${theme.fontWeights.semibold};
    font-size: 15px;
    text-transform: uppercase;
    color: ${theme.colors.white};
    font-family: 'Roboto';
    text-align: center;
    width: 100%;

    &:before {
      content: '';
      position: absolute;
      top: 50%;
      left: 0;

      transform: translateY(-50%);
      display: block;
      width: 1px;
      height: 40px;
      background: ${theme.colors.getColor('neutral-purewhite')};
    }

    @media screen and (max-width: ${theme.breakpoints.get('medium', 'max')}px) {
      ${srOnly}
    }
  `}
`

export const WrapperIcon = styled.div`
  display: flex;
  justify-content: center;
  margin-right: 12px;
`
